import { useState, useRef, useEffect } from "react";
import { 
  X, 
  Send, 
  Loader2, 
  User,
  Sparkles,
  Search,
  BookOpen,
  Minimize2,
  Maximize2,
  Trash2,
  Play,
  Wrench,
  Package,
  ChevronRight,
  Zap,
  Clock,
  Volume2,
  VolumeX
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Isaac avatar - Digital Isaac image
const ISAAC_AVATAR = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/fe24d5e280d09d6a7d0c6d06f6b149605eaa9a0fa1be0b67e18ee25456bf0b6f.png";

// Markdown components - defined outside to avoid re-creation on each render
const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="text-cyan-300 font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2">{children}</ul>,
  li: ({ children }) => <li className="text-slate-300">{children}</li>,
  code: ({ children }) => <code className="bg-slate-700 px-1 py-0.5 rounded text-cyan-300">{children}</code>,
  h1: ({ children }) => <h1 className="text-lg font-bold text-white mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-white mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold text-white mb-1">{children}</h3>,
};

export function IsaacChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Isaac, your AI assistant for healthcare facility management. I can help you with:\n\n- **Platform questions** - How to use AskIsaac features\n- **Parts search** - Find equipment in your database\n- **AAMI ST108** - Sterile processing standards\n- **Maintenance schedules** - Equipment service intervals\n\nHow can I help you today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showDemoMenu, setShowDemoMenu] = useState(false);
  const [runningDemo, setRunningDemo] = useState(null);
  const [currentDemoStep, setCurrentDemoStep] = useState(0);
  // Voice-speaking-Isaac state
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("isaac_voice_on") === "1"; } catch { return false; }
  });
  const [speakingMsgIdx, setSpeakingMsgIdx] = useState(null);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem("isaac_voice_on", voiceOn ? "1" : "0"); } catch { /* ignore */ }
    if (!voiceOn && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setSpeakingMsgIdx(null);
    }
  }, [voiceOn]);

  // Speak a message via the /api/isaac/tts endpoint
  const speakMessage = async (text, msgIdx) => {
    if (!text) return;
    try {
      // Stop anything currently playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const resp = await fetch(`${API_URL}/api/isaac/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) throw new Error(`TTS ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onplay = () => setSpeakingMsgIdx(msgIdx);
      audio.onended = () => {
        setSpeakingMsgIdx((cur) => (cur === msgIdx ? null : cur));
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setSpeakingMsgIdx((cur) => (cur === msgIdx ? null : cur));
        URL.revokeObjectURL(url);
      };
      audioRef.current = audio;
      await audio.play();
    } catch (err) {
      console.warn("Isaac speak failed:", err);
      setSpeakingMsgIdx(null);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSpeakingMsgIdx(null);
  };

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Generate or retrieve session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem("isaac_session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = `isaac-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("isaac_session_id", newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  // Allow other components to open the chat via a custom event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("askisaac:open", handler);
    return () => window.removeEventListener("askisaac:open", handler);
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/isaac/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          user_id: localStorage.getItem("user_id") || null
        })
      });

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const data = await response.json();
      
      // Update session ID if returned
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("isaac_session_id", data.session_id);
      }

      const assistantMessage = {
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString(),
        sources: data.sources
      };

      setMessages(prev => {
        const next = [...prev, assistantMessage];
        // If voice is on, speak this new reply
        if (voiceOn && assistantMessage.content) {
          const idx = next.length - 1;
          setTimeout(() => speakMessage(assistantMessage.content, idx), 50);
        }
        return next;
      });
    } catch (error) {
      console.error("Isaac chat error:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: "assistant",
      content: "Hi! I'm Isaac, your AI assistant. How can I help you today?",
      timestamp: new Date().toISOString()
    }]);
    // Generate new session
    const newSessionId = `isaac-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("isaac_session_id", newSessionId);
    setSessionId(newSessionId);
    setShowDemoMenu(false);
    setRunningDemo(null);
    setCurrentDemoStep(0);
  };

  // Start a demo simulation
  const startDemo = async (demoType) => {
    setShowDemoMenu(false);
    setIsLoading(true);
    setRunningDemo(demoType);
    setCurrentDemoStep(0);

    // Add intro message
    setMessages(prev => [...prev, {
      role: "assistant",
      content: `🎬 **Starting ${demoType === 'maintenance' ? 'Maintenance' : 'Backorder'} Lifecycle Simulation**\n\nWatch how AskIsaac handles the entire process from start to finish...`,
      timestamp: new Date().toISOString(),
      isDemo: true
    }]);

    try {
      const response = await fetch(`${API_URL}/api/isaac/demo/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ demo_type: demoType, session_id: sessionId })
      });

      if (!response.ok) throw new Error("Failed to start demo");

      const data = await response.json();
      
      // Display steps with delays
      for (let i = 0; i < data.steps.length; i++) {
        const step = data.steps[i];
        setCurrentDemoStep(i + 1);
        
        // Wait for the delay (skip first step)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, step.delay_ms || 2500));
        }
        
        // Add the step as a message
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `${step.icon} **STEP ${step.step}: ${step.title}**\n━━━━━━━━━━━━━━━━━━━━━━━\n\n${step.content}`,
          timestamp: new Date().toISOString(),
          isDemo: true,
          demoStep: step.step
        }]);
      }

      // Add completion message
      await new Promise(resolve => setTimeout(resolve, 2000));
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `━━━━━━━━━━━━━━━━━━━━━━━\n🎬 **SIMULATION COMPLETE**\n━━━━━━━━━━━━━━━━━━━━━━━\n\n✅ **What You Just Saw:**\n${demoType === 'maintenance' 
          ? '• End-to-end maintenance workflow\n• Real-time multi-stakeholder communication\n• GPS-verified contractor tracking\n• HIPAA-compliant audit logging\n• Automated financial processing' 
          : '• Automated vendor network sourcing\n• 3-quote compliance automation\n• Transparent markup structure\n• Real-time logistics tracking\n• Full audit trail for every action'}\n\n📈 **AskIsaac Value Delivered:**\n${demoType === 'maintenance'
          ? '• 70% faster response vs. manual dispatch\n• 100% audit compliance\n• Complete visibility for all parties'
          : '• 4 weeks → 24 hours delivery time\n• 100% procurement compliance\n• Platform revenue captured'}\n\nWould you like to try another demo or ask me anything?`,
        timestamp: new Date().toISOString(),
        isDemo: true
      }]);

    } catch (error) {
      console.error("Demo error:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I couldn't start the demo simulation. Please try again.",
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
      setRunningDemo(null);
      setCurrentDemoStep(0);
    }
  };

  // Quick actions menu items
  const quickActions = [
    { icon: Search, text: "Search for autoclaves", query: "Search for autoclaves in the database" },
    { icon: BookOpen, text: "AAMI ST108 basics", query: "What are the key requirements of AAMI ST108?" },
    { icon: Sparkles, text: "Autoclave maintenance", query: "What is the recommended maintenance schedule for autoclaves?" }
  ];

  // Demo menu options
  const demoOptions = [
    {
      id: "maintenance",
      icon: Wrench,
      title: "Maintenance Lifecycle",
      subtitle: "HVAC/Plumbing repair journey",
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: "backorder",
      icon: Package,
      title: "Backorder Lifecycle",
      subtitle: "Procurement journey",
      color: "from-violet-500 to-purple-500"
    }
  ];

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          data-testid="isaac-chat-button"
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-lg opacity-60 group-hover:opacity-80 transition-opacity" />
            
            {/* Button */}
            <div className="relative flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-full shadow-2xl transition-all transform group-hover:scale-105">
              <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/30">
                <img src={ISAAC_AVATAR} alt="Isaac" className="w-full h-full object-cover" />
              </div>
              <span className="font-semibold">Ask Isaac</span>
              <Sparkles className="w-4 h-4 text-cyan-200" />
            </div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 bg-slate-900 border border-slate-700 shadow-2xl flex flex-col transition-all duration-300 ${
            isExpanded 
              ? "inset-4 md:inset-8 rounded-2xl" 
              : "bottom-6 right-6 w-[420px] h-[650px] max-h-[85vh] rounded-2xl"
          }`}
          data-testid="isaac-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-cyan-500/50">
                  <img src={ISAAC_AVATAR} alt="Isaac" className="w-full h-full object-cover" />
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900" />
              </div>
              <div>
                <h3 className="text-white font-semibold flex items-center gap-2">
                  Ask Isaac
                  <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full font-medium">AI</span>
                </h3>
                <p className="text-xs text-slate-400">Healthcare Operations Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (voiceOn && speakingMsgIdx !== null) {
                    stopSpeaking();
                  }
                  setVoiceOn((v) => !v);
                }}
                className={`hover:bg-slate-700 ${voiceOn ? "text-cyan-300" : "text-slate-400 hover:text-white"}`}
                title={voiceOn ? "Voice: on — click to mute" : "Voice: off — click to have Isaac speak"}
                data-testid="isaac-chat-voice-toggle"
                aria-pressed={voiceOn}
              >
                {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDemoMenu(!showDemoMenu)}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                title="Demo Center"
              >
                <Play className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={clearChat}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Demo Menu Overlay */}
          {showDemoMenu && (
            <div className="absolute inset-0 bg-slate-900/95 z-10 rounded-2xl flex flex-col p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Demo Center
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowDemoMenu(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-slate-400 text-sm mb-6">
                Experience full lifecycle simulations of how AskIsaac streamlines hospital operations.
              </p>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Full Lifecycle Demos</p>
                {demoOptions.map((demo) => (
                  <button
                    key={demo.id}
                    onClick={() => startDemo(demo.id)}
                    disabled={isLoading}
                    className={`w-full p-4 rounded-xl bg-gradient-to-r ${demo.color} bg-opacity-10 border border-slate-700 hover:border-slate-500 transition-all group text-left`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${demo.color} flex items-center justify-center`}>
                        <demo.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold">{demo.title}</h4>
                        <p className="text-slate-400 text-xs">{demo.subtitle}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xs text-slate-400 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Each demo takes ~30 seconds and shows all stakeholder interactions
                </p>
              </div>
            </div>
          )}

          {/* Running Demo Progress */}
          {runningDemo && (
            <div className="px-4 py-2 bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border-b border-slate-700">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                <span className="text-cyan-400 font-medium">
                  Running {runningDemo === 'maintenance' ? 'Maintenance' : 'Backorder'} Demo
                </span>
                <span className="text-slate-500 ml-auto">Step {currentDemoStep}/6</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1 mt-2">
                <div 
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 h-1 rounded-full transition-all duration-500"
                  style={{ width: `${(currentDemoStep / 6) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ${
                    msg.role === "user" 
                      ? "bg-gradient-to-br from-violet-500 to-purple-600" 
                      : "border-2 border-cyan-500/30"
                  }`}>
                    {msg.role === "user" ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <img src={ISAAC_AVATAR} alt="Isaac" className="w-full h-full object-cover" />
                    )}
                  </div>

                  {/* Message */}
                  <div className={`max-w-[80%] ${msg.role === "user" ? "text-right" : ""}`}>
                    <div className={`rounded-2xl px-4 py-3 ${
                      msg.role === "user"
                        ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white"
                        : msg.isError
                          ? "bg-red-900/30 border border-red-500/30 text-red-200"
                          : msg.isDemo
                            ? "bg-gradient-to-r from-slate-800 to-slate-800/80 border border-cyan-500/20 text-slate-200"
                            : "bg-slate-800 text-slate-200"
                    }`}>
                      {msg.role === "assistant" ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown components={markdownComponents}>
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p>{msg.content}</p>
                      )}
                    </div>

                    {/* Per-message speak / stop button (assistant only) */}
                    {msg.role === "assistant" && !msg.isError && msg.content && (
                      <button
                        type="button"
                        onClick={() => {
                          if (speakingMsgIdx === idx) stopSpeaking();
                          else speakMessage(msg.content, idx);
                        }}
                        className={`mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                          speakingMsgIdx === idx
                            ? "text-cyan-300"
                            : "text-slate-500 hover:text-cyan-300"
                        }`}
                        data-testid={`isaac-chat-msg-speak-${idx}`}
                      >
                        {speakingMsgIdx === idx ? (
                          <>
                            <span className="flex gap-0.5 items-end h-2.5">
                              <span className="w-0.5 bg-cyan-300 animate-pulse" style={{ height: "40%", animationDelay: "0ms" }} />
                              <span className="w-0.5 bg-cyan-300 animate-pulse" style={{ height: "100%", animationDelay: "120ms" }} />
                              <span className="w-0.5 bg-cyan-300 animate-pulse" style={{ height: "60%", animationDelay: "240ms" }} />
                              <span className="w-0.5 bg-cyan-300 animate-pulse" style={{ height: "80%", animationDelay: "360ms" }} />
                            </span>
                            Speaking · tap to stop
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-3 h-3" />
                            Hear it
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Sources */}
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {msg.sources.map((source, sidx) => (
                          <span 
                            key={sidx}
                            className="text-[10px] px-2 py-0.5 bg-slate-700/50 text-slate-400 rounded-full"
                          >
                            {source.type === "database" ? "📊 Database" : 
                             source.type === "standards" ? "📋 AAMI ST108" : 
                             source.type === "water_testing" ? "💧 Water Testing" : source.type}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Timestamp */}
                    <p className={`text-[10px] text-slate-500 mt-1 ${msg.role === "user" ? "text-right" : ""}`}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && !runningDemo && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-cyan-500/30">
                    <img src={ISAAC_AVATAR} alt="Isaac" className="w-full h-full object-cover" />
                  </div>
                  <div className="bg-slate-800 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                      <span className="text-slate-400 text-sm">Isaac is thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Quick Actions */}
          {messages.length <= 1 && !showDemoMenu && (
            <div className="px-4 py-2 border-t border-slate-700/50">
              <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowDemoMenu(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-cyan-900/50 to-blue-900/50 hover:from-cyan-800/50 hover:to-blue-800/50 border border-cyan-500/30 text-cyan-400 rounded-full text-xs transition-colors"
                >
                  <Play className="w-3 h-3" />
                  Run Demo
                </button>
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInput(action.query);
                      setTimeout(sendMessage, 100);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full text-xs transition-colors"
                  >
                    <action.icon className="w-3 h-3" />
                    {action.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-700 bg-slate-800/50 rounded-b-2xl">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Isaac anything..."
                className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:ring-cyan-500 focus:border-cyan-500"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-4"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2 text-center">
              Powered by AskIsaac AI • Press Enter to send
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default IsaacChat;
